/** Storage processing and stable media aliases share the API origin. */
export const STORAGE_API_HOST = 'api.fxcdn.link';
export const STORAGE_API_ORIGIN = `https://${STORAGE_API_HOST}`;
export const STORAGE_MEDIA_ALIAS_ORIGINS = Object.freeze([
    STORAGE_API_ORIGIN,
    'https://cdn.influ.gg',
    'https://api-cdn.influ.gg',
]);

/** Keep legacy records usable without trusting lookalike or credential-bearing URLs. */
export function isStorageMediaAliasUrl(url: URL): boolean {
    return !url.username && !url.password && STORAGE_MEDIA_ALIAS_ORIGINS.includes(url.origin);
}

/** Match relative metadata and absolute records saved under either legacy hostname. */
export function storageMediaLookupCandidates(url: URL): Array<string> {
    if (!isStorageMediaAliasUrl(url) || !/^\/media\/(?:full|thumbnail|icon)\//.test(url.pathname)) return [url.href];
    return [...new Set([
        url.href,
        url.pathname,
        ...STORAGE_MEDIA_ALIAS_ORIGINS.map((origin) => new URL(`${origin}${url.pathname}`).href),
    ])];
}

/**
 * Builds anchored URL prefixes that find stored media by upload path.
 *
 * Posts can store a file under any storage alias or as a relative path, and with a different
 * extension than the upload record. Anchored, case-sensitive prefixes cover those forms while
 * staying index-bounded; an unanchored pattern would scan the whole media URL index.
 */
export function storageMediaUrlPrefixes(publicPaths: ReadonlyArray<string>): Array<RegExp> {
    const urlPrefixes = new Set(publicPaths.flatMap((publicPath) => {
        const {pathname} = new URL(publicPath, STORAGE_API_ORIGIN);
        const pathWithoutExtension = pathname.replace(/(?<=\/[^/]*\.)[^./]+$/, '');
        return [...STORAGE_MEDIA_ALIAS_ORIGINS, ''].map((origin) => `${origin}${pathWithoutExtension}`);
    }));
    return Array.from(urlPrefixes, (urlPrefix) => new RegExp(`^${escapeRegex(urlPrefix)}`));
}

function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
